import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { LayoutDashboard, CalendarDays, ClipboardList, Users, Sparkles, SlidersHorizontal, UserCog, CreditCard, FileText, TicketPercent, BarChart3, ScrollText, ShieldCheck, Briefcase, Menu, Loader2, Mail, CalendarPlus } from "lucide-react";
import { useAdminApi, errMsg } from "../../lib/api";
import { Logo } from "../../components/site/SiteLayout";

export const NAV = [
  ["/admin", "Overview", LayoutDashboard, ["admin", "manager"]],
  ["/admin/calendar", "Calendar", CalendarDays, ["admin", "manager"]],
  ["/admin/bookings", "Bookings", ClipboardList, ["admin", "manager"]],
  ["/admin/new-booking", "New booking", CalendarPlus, ["admin", "manager"]],
  ["/admin/customers", "Customers", Users, ["admin", "manager"]],
  ["/admin/quotes", "Custom quotes", FileText, ["admin", "manager"]],
  ["/admin/employees", "Employees", UserCog, ["admin", "manager"]],
  ["/admin/messages", "Messages", Mail, ["admin", "manager"]],
  ["/admin/services", "Services & add-ons", Sparkles, ["admin"]],
  ["/admin/pricing", "Pricing & schedule", SlidersHorizontal, ["admin"]],
  ["/admin/coupons", "Coupons", TicketPercent, ["admin"]],
  ["/admin/payments", "Payments", CreditCard, ["admin"]],
  ["/admin/reports", "Revenue reports", BarChart3, ["admin"]],
  ["/admin/audit", "Audit log", ScrollText, ["admin"]],
  ["/admin/users", "Staff access", ShieldCheck, ["admin"]],
  ["/admin/my-jobs", "My jobs", Briefcase, ["admin", "manager", "cleaner"]],
];

export const useAdmin = () => useOutletContext();

function Shell() {
  const call = useAdminApi();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { call("get", "/me").then(setMe).catch((e) => setErr(errMsg(e))); }, [call]);
  if (err) return <div className="p-10 text-red-600">{err}</div>;
  if (!me) return <div className="h-screen grid place-items-center"><Loader2 className="animate-spin text-sky-600" /></div>;
  if (me.role === "customer") return <Navigate to="/account" replace />;
  const links = NAV.filter((n) => n[3].includes(me.role));
  const path = location.pathname.replace(/\/$/, "");
  const route = NAV.find((n) => n[0] === path);
  const denied = route && !route[3].includes(me.role);
  const side = (
    <nav className="space-y-1">
      {links.map(([to, label, I]) => (
        <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-sky-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>
          <I size={17} />{label}
        </NavLink>
      ))}
    </nav>
  );
  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">
      <aside className={`${open ? "fixed inset-0 z-40 w-64" : "hidden"} lg:static lg:flex w-64 bg-slate-900 flex-col justify-between shrink-0 p-4 overflow-y-auto`}>
        <div className="space-y-6"><Logo light />{side}</div>
        <p className="text-xs text-slate-500 mt-6">{me.email} · <span className="uppercase" data-testid="admin-role-label">{me.role}</span></p>
      </aside>
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
          <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu /></button>
          <span className="font-display font-semibold text-slate-800 hidden lg:block">Mainliz Admin</span>
          <UserButton />
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {me.role === "cleaner" && path !== "/admin/my-jobs" ? <Navigate to="/admin/my-jobs" replace /> : denied ? <p className="text-slate-600" data-testid="admin-access-denied">You don't have access to this page. Ask an owner if you need it.</p> : <Outlet context={{ me, call }} />}
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <>
      <Show when="signed-in"><Shell /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}

export const H = ({ title, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3"><h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1><div className="flex flex-wrap gap-2">{children}</div></div>
);

export const StatusBadge = ({ s }) => {
  const c = { confirmed: "bg-emerald-100 text-emerald-800", assigned: "bg-teal-100 text-teal-800", completed: "bg-slate-200 text-slate-800", in_progress: "bg-sky-100 text-sky-800", cancelled: "bg-red-100 text-red-700", expired: "bg-red-50 text-red-500", paid: "bg-emerald-100 text-emerald-800", refunded: "bg-red-100 text-red-700", partially_refunded: "bg-amber-100 text-amber-800", new: "bg-sky-100 text-sky-800", quoted: "bg-indigo-100 text-indigo-800", booked: "bg-emerald-100 text-emerald-800" }[s] || "bg-amber-100 text-amber-800";
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${c}`}>{String(s).replace(/_/g, " ")}</span>;
};

export const Table = ({ cols, rows, onRow, testid, empty = "Nothing here yet." }) => (
  <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-500 text-left"><tr>{cols.map((c) => <th key={c[0]} className="px-4 py-3 font-medium whitespace-nowrap">{c[0]}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-slate-400">{empty}</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.id || i} onClick={onRow ? () => onRow(r) : undefined} className={`border-t border-slate-100 ${onRow ? "cursor-pointer hover:bg-sky-50/50" : ""}`} data-testid={`${testid}-row-${i}`}>
            {cols.map((c) => <td key={c[0]} className="px-4 py-3 whitespace-nowrap">{c[1](r)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
